import { useRequiredContext } from "@redotech/react-util/context";
import { WidgetClient, getOrders } from "@redotech/redo-api-client/widget";
import { Order } from "@redotech/redo-model/order";
import { GetOrdersRequest } from "@redotech/redo-model/return-flow";
import { RedoClientContext } from "@redotech/redo-web/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";
import { AuthContext } from "../contexts/auth";
import { TeamInfoContext } from "../contexts/team-info";

export const useOrders = (): {
  orders: Order[] | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  updateOrder: (updatedOrder: Order) => void;
} => {
  const teamInfo = useRequiredContext(TeamInfoContext);
  const auth = useContext(AuthContext);
  const client = useRequiredContext(RedoClientContext);
  const widgetClient = new WidgetClient(client, teamInfo.widgetSlug);
  const queryClient = useQueryClient();

  const {
    data: orders,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["orders", teamInfo.id, auth?.customerEmail],
    queryFn: async ({ signal }): Promise<Order[]> => {
      if (!auth) {
        return [];
      }

      let pullNextPage = true;
      let page = 1;
      const orders: Order[] = [];

      while (pullNextPage) {
        const ordersRequest: GetOrdersRequest = {
          email: auth.customerEmail,
          getAllStatuses: true,
          customerWidgetAuthToken: auth.token,
          page,
        };
        const data = await getOrders(widgetClient, ordersRequest, { signal });
        const orderData = data.orders.data;

        for (const item of orderData) {
          orders.push(item);
        }

        if (page < data.orders.pages) {
          page++;
        } else {
          pullNextPage = false;
        }
      }
      return orders;
    },
    enabled: !!auth && !!teamInfo.id,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });

  const updateOrder = (updatedOrder: Order) => {
    const queryKey = ["orders", teamInfo.id, auth?.customerEmail];

    queryClient.setQueryData<Order[]>(queryKey, (oldOrders) => {
      if (!oldOrders) return oldOrders;

      return oldOrders.map((order) =>
        order._id === updatedOrder._id ? updatedOrder : order,
      );
    });
  };

  return {
    orders,
    isLoading,
    error: error as Error | null,
    refetch,
    updateOrder,
  };
};
